import React, { useEffect, useMemo } from 'react';
import { Container, Offcanvas } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Alerts from '../alert/Alerts';
import { useAlerts } from '../alert/AlertContext';
import { useAuthenticatedSidebar } from '../hooks/useAuthenticatedSidebar';
import { useNavGroupsExpandedForPath } from '../hooks/useNavGroupsExpandedForPath';
import type { IUser } from '../models/auth';
import AppNavMenu from '../navigation/AppNavMenu';
import { filterNavMenuForUser } from '../navigation/navItems';
import AppBreadcrumbs from '../routing/Breadcrumbs';
import AuthenticatedNavbar from './AuthenticatedNavbar';

export default function AuthenticatedLayout(props: {
  user: IUser;
  children: React.ReactNode;
  navbarBrandAddon?: React.ReactNode;
}): React.ReactElement {
  const { user, children, navbarBrandAddon } = props;
  const { clearAll: clearAllAlerts } = useAlerts();
  const location = useLocation();
  const {
    lgUp,
    desktopSidebarOpen,
    mobileNavOpen,
    handleNavToggle,
    setMobileNavOpen,
  } = useAuthenticatedSidebar();

  useEffect(() => {
    clearAllAlerts();
  }, [clearAllAlerts, location.pathname, location.search]);

  const navEntries = useMemo(() => filterNavMenuForUser(user), [user]);
  const { expandedGroupLabels, toggleNavGroup } = useNavGroupsExpandedForPath(
    location.pathname,
    navEntries,
  );

  const navToggleAriaExpanded = lgUp ? desktopSidebarOpen : mobileNavOpen;

  return (
    <div className="min-vh-100 d-flex flex-column">
      <AuthenticatedNavbar
        user={user}
        navbarBrandAddon={navbarBrandAddon}
        onNavToggle={handleNavToggle}
        navToggleAriaExpanded={navToggleAriaExpanded}
      />

      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        <aside
          className={`d-none flex-column flex-shrink-0 bg-dark border-end border-secondary overflow-auto ${
            desktopSidebarOpen ? 'd-lg-flex' : 'd-lg-none'
          }`}
          style={{ width: 250 }}>
          <AppNavMenu
            entries={navEntries}
            variant="dark"
            expandedGroupLabels={expandedGroupLabels}
            onToggleGroup={toggleNavGroup}
          />
        </aside>

        <div className="flex-grow-1 min-w-0 d-flex flex-column overflow-auto">
          <Container fluid className="flex-grow-1 py-2">
            <AppBreadcrumbs />
            <Alerts />
            {children}
          </Container>
        </div>
      </div>

      <Offcanvas
        id="app-side-nav"
        show={mobileNavOpen}
        onHide={() => setMobileNavOpen(false)}
        placement="start">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Navigation</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <AppNavMenu
            entries={navEntries}
            variant="light"
            expandedGroupLabels={expandedGroupLabels}
            onToggleGroup={toggleNavGroup}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
}
